import { supabase } from "@/integrations/supabase/client";

export function keyOf(semester: string, klass: string, moduleCode: string, sessionNo: number | string): string {
  return `${semester}||${klass}||${moduleCode}||${sessionNo}`;
}

export interface SessionNoProbe {
  semester: string;
  class: string;
  module_code: string;
  session_no: number;
}

/**
 * Returns a Set of "semester||class||module||session_no" keys that already exist
 * in module_sessions for the (semester, class, module_code) tuples represented by `rows`.
 * Optionally excludes a row id (used when editing).
 */
export async function findDuplicateSessionNos(
  rows: SessionNoProbe[],
  excludeId?: string,
): Promise<Set<string>> {
  const result = new Set<string>();
  if (!rows.length) return result;

  // Distinct tuples
  const tupleMap = new Map<string, { semester: string; class: string; module_code: string }>();
  for (const r of rows) {
    const k = `${r.semester}||${r.class}||${r.module_code}`;
    if (!tupleMap.has(k)) tupleMap.set(k, { semester: r.semester, class: r.class, module_code: r.module_code });
  }
  const tuples = [...tupleMap.values()];
  const semesters = [...new Set(tuples.map((t) => t.semester))];
  const classes = [...new Set(tuples.map((t) => t.class))];
  const codes = [...new Set(tuples.map((t) => t.module_code))];

  let q: any = supabase
    .from("module_sessions")
    .select("id, semester, class, module_code, session_no")
    .in("semester", semesters)
    .in("class", classes)
    .in("module_code", codes);
  if (excludeId) q = q.neq("id", excludeId);

  const { data, error } = await q;
  if (error) throw error;

  // Filter to actual matching tuples (since .in() does a cartesian filter)
  const tupleKeys = new Set(tupleMap.keys());
  for (const r of (data ?? []) as any[]) {
    const tk = `${r.semester}||${r.class}||${r.module_code}`;
    if (!tupleKeys.has(tk)) continue;
    result.add(keyOf(r.semester, r.class, r.module_code, r.session_no));
  }
  return result;
}
